class CompareProductPopup extends HTMLElement {
  constructor() {
    super();
    this.compareItems = this.readCompareItems();
    this.compareProducts = [];
    this.compareHeadings = null;
    this.init();
  }

  readCompareItems() {
    try {
      const items = JSON.parse(localStorage.getItem("compareItem") || "[]");
      return Array.isArray(items) ? [...new Set(items.filter((item) => typeof item === "string" && item))] : [];
    } catch (_) {
      return [];
    }
  }

  saveCompareItems() {
    // Storage may be disabled or full; comparison remains usable for this page.
    try {
      localStorage.setItem("compareItem", JSON.stringify(this.compareItems));
    } catch (_) {}
  }

  connectedCallback() {
    const container = document.getElementById("MainContent") || document.body;
    if (!container) return;
    this.compareObserver?.disconnect();
    this.compareObserver = new MutationObserver((records) => {
      const hasNewCards = records.some((record) => Array.from(record.addedNodes).some((node) =>
        node.nodeType === 1 && (node.matches("[data-product-compare-handle]") || node.querySelector("[data-product-compare-handle]"))
      ));
      if (hasNewCards) this.setLocalStorageProductForCompare();
    });
    this.compareObserver.observe(container, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.compareObserver?.disconnect();
  }

  init() {
    document.addEventListener("click", this.handleClick.bind(this));
    this.initCompareProduct();
    this.setupEventListeners();
    this.setLocalStorageProductForCompare();
  }

  getCompareDrawer() {
    return document.querySelector("#halo-compare-product-popup");
  }

  getCompareTable() {
    return document.querySelector("[data-compare-product-popup] .compareTable");
  }

  handleClick(event) {
    const target = event.target;
    const opener = target.closest?.("[data-compare-link]");
    const removeButton = target.closest?.("[data-compare-remove]");
    if (opener) {
      event.preventDefault();
      this.handleCompareLink(opener);
    } else if (removeButton) {
      event.preventDefault();
      this.handleCompareRemove(removeButton);
    }
  }

  initCompareProduct() {
    const closeBtn = document.querySelector("[data-close-compare-product-popup]");
    if (window.compare.show) {
      this.setLocalStorageProductForCompare();
      const drawer = this.getCompareDrawer();
      closeBtn?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        drawer?.close?.();
      });
    }
  }

  setupEventListeners() {
    document.addEventListener("change", (event) => {
      if (event.target.matches("[data-product-compare] input")) this.handleCompareChange(event);
    });
  }

  setLocalStorageProductForCompare() {
    const items = document.querySelectorAll("[data-product-compare-handle]");
    if (!this.compareItems) return;

    items.forEach((element) => {
      const handle = element.dataset.productCompareHandle;
      const compareIcon = element.querySelector(".compare-button");
      const legacyIcon = element.querySelector(".compare-icon");
      const text = element.querySelector(".text .text-inner");
      const input = element.querySelector("input");
      const label = element.querySelector("label");
      const checked = this.compareItems.includes(handle);
      const labelText = checked ? window.compare.added : window.compare.add;

      compareIcon?.classList.toggle("is-checked", checked);
      legacyIcon?.classList.toggle("is-checked", checked);
      if (text) text.textContent = labelText;
      if (input) {
        input.checked = checked;
        input.title = labelText;
      }
      if (label) label.title = labelText;
      if (compareIcon) compareIcon.title = labelText;
    });

    this.updateCounterCompare();
  }

  handleCompareChange(event) {
    const target = event.target;
    if (target.checked) {
      this.incrementCounterCompare(target.value);
    } else {
      this.decrementCounterCompare(target.value);
    }
  }

  handleCompareLink(opener) {
    if (this.compareItems.length <= 1) {
      alert(window.compare.message);
      return false;
    }
    if (opener?.classList.contains("loading")) return;
    opener?.classList.add("loading");
    opener?.querySelector(".loading__spinner")?.classList.remove("hidden");
    return this.updateContentCompareProduct(this.compareItems, 0, opener);
  }

  handleCompareRemove(target) {
    const id = target.dataset.compareItem;
    const index = this.compareProducts.findIndex((product) => product.id === id);
    if (index === -1) return;

    const [product] = this.compareProducts.splice(index, 1);
    this.decrementCounterCompare(product.handle);
    const table = this.getCompareTable();
    if (table) this.renderCompareTable(table);

    if (this.compareProducts.length === 0) {
      this.getCompareDrawer()?.close?.();
      return;
    }

    const columns = table?.querySelectorAll("thead .compareTable-product");
    const nextColumn = columns?.[Math.min(index, this.compareProducts.length - 1)];
    const nextControl = nextColumn?.querySelector("[data-compare-remove]") || nextColumn?.querySelector("a, button, input, [tabindex]");
    nextControl?.focus?.({ preventScroll: true });
  }

  updateCounterCompare() {
    const compareLink = document.querySelector("[data-compare-link]");
    if (!compareLink) return;

    if (this.compareItems.length > 1) {
      compareLink.parentElement.classList.add("is-show");
      compareLink.querySelector("span.countPill").textContent = this.compareItems.length;
    } else {
      compareLink.parentElement.classList.remove("is-show");
    }
  }

  incrementCounterCompare(item) {
    if (!this.compareItems.includes(item)) {
      this.compareItems.push(item);
      this.saveCompareItems();
    }
    this.setLocalStorageProductForCompare();
  }

  decrementCounterCompare(item) {
    const index = this.compareItems.indexOf(item);
    if (index > -1) {
      this.compareItems.splice(index, 1);
      this.saveCompareItems();
    }
    this.setLocalStorageProductForCompare();
  }

  cacheCompareHeadings(table) {
    if (this.compareHeadings) return;
    const headings = Array.from(table.querySelectorAll("thead th"), (heading) => heading.textContent.trim());
    if (headings.length !== 7 || headings.some((heading) => !heading)) {
      throw new Error("Invalid compare table headings");
    }
    this.compareHeadings = headings;
  }

  parseCompareProduct(data, handle) {
    const body = document.createElement("tbody");
    body.innerHTML = data;
    const row = body.firstElementChild;
    if (body.children.length !== 1 || !row?.matches("tr.compare-table-row") || row.cells.length !== 7 || !row.dataset.productCompareId || row.dataset.compareProductHandle !== handle) {
      throw new Error("Invalid compare product row");
    }
    if (Array.from(row.cells).some((cell) => cell.tagName !== "TD") || !row.cells[0].classList.contains("compareTable-product") || row.cells[0].querySelector("[data-compare-remove]")?.dataset.compareItem !== row.dataset.productCompareId) {
      throw new Error("Invalid compare product cells");
    }
    return {
      id: row.dataset.productCompareId,
      handle,
      cells: Array.from(row.cells, (cell) => cell.cloneNode(true))
    };
  }

  renderCompareTable(table) {
    const head = document.createElement("thead");
    const headerRow = document.createElement("tr");
    headerRow.className = "compare-table-row compareTable-product-row";
    const productLabel = document.createElement("th");
    productLabel.className = "compareTable-heading compareTable-label";
    productLabel.scope = "col";
    productLabel.textContent = this.compareHeadings[0];
    headerRow.append(productLabel);

    this.compareProducts.forEach((product) => {
      const source = product.cells[0].cloneNode(true);
      const cell = document.createElement("th");
      cell.className = source.className;
      cell.classList.add("compareTable-product");
      cell.scope = "col";
      cell.dataset.productCompareId = product.id;
      cell.dataset.compareProductHandle = product.handle;
      cell.append(...Array.from(source.childNodes));
      headerRow.append(cell);
    });
    head.append(headerRow);

    const body = document.createElement("tbody");
    this.compareHeadings.slice(1).forEach((heading, index) => {
      const row = document.createElement("tr");
      row.className = "compare-table-row compareTable-attribute-row";
      const label = document.createElement("th");
      label.className = "compareTable-heading compareTable-label";
      label.scope = "row";
      label.textContent = heading;
      row.append(label);

      this.compareProducts.forEach((product) => {
        const cell = product.cells[index + 1].cloneNode(true);
        cell.dataset.productCompareId = product.id;
        cell.dataset.compareProductHandle = product.handle;
        row.append(cell);
      });
      body.append(row);
    });

    table.classList.add("compareTable--columns");
    table.classList.remove("hidden");
    const scrollRegion = table.closest(".compare-table-scroll");
    if (scrollRegion) {
      scrollRegion.hidden = false;
      scrollRegion.classList.remove("hidden");
    }
    table.style.setProperty("--compare-product-count", this.compareProducts.length);
    table.replaceChildren(head, body);
  }

  setCompareError(visible) {
    const error = this.getCompareDrawer()?.querySelector("[data-compare-error]");
    if (error) {
      error.hidden = !visible;
      error.classList.toggle("hidden", !visible);
    }
    return error;
  }

  async updateContentCompareProduct(list, count, opener) {
    const table = this.getCompareTable();
    const drawer = this.getCompareDrawer();
    const rootUrl = (window.routes?.root || window.routes?.shop_origin || window.shopUrl || "").replace(/\/$/, "");
    const handles = list.slice(count);

    try {
      if (!table) throw new Error("Compare table is unavailable");
      this.cacheCompareHeadings(table);
      this.setCompareError(false);
      const products = [];
      for (const handle of handles) {
        const response = await fetch(`${rootUrl}/products/${handle}?view=ajax_product_card_compare`);
        if (!response.ok) throw new Error(`Compare product request failed: ${response.status}`);
        const product = this.parseCompareProduct(await response.text(), handle);
        if (products.some((item) => item.id === product.id)) throw new Error("Duplicate compare product row");
        products.push(product);
      }
      if (products.length === 0) throw new Error("Compare product list is empty");
      this.compareProducts = products;
      this.renderCompareTable(table);
      drawer?.open?.(opener);
    } catch (error) {
      console.error("Error fetching compare product:", error);
      if (this.setCompareError(true)) {
        table?.classList.add("hidden");
        const scrollRegion = table?.closest(".compare-table-scroll");
        if (scrollRegion) {
          scrollRegion.hidden = true;
          scrollRegion.classList.add("hidden");
        }
        drawer?.open?.(opener);
      }
    } finally {
      opener?.classList.remove("loading");
      opener?.querySelector(".loading__spinner")?.classList.add("hidden");
    }
  }
}
if (!customElements.get("compare-product-popup")) customElements.define("compare-product-popup", CompareProductPopup);

